import React, { useCallback, useMemo, useRef } from "react"
import { Immutable, State, useHookstate } from "@hookstate/core"
import { Validation } from "@hookstate/validation"
import { Col, Row } from "react-bootstrap"
import { useDrag, useDrop } from "react-dnd"
import { useExhibitorDataState } from "../../globalStates/ExhibitorDataState"
import { useLanguageState } from "../../globalStates/LanguageState"
import { RefPersonFunctions } from "../../globalStates/ReferenceDataState"
import { getElementFieldValue, makeCheckHandler, makeSetHandler, makeValidationHandler } from "../../globalStates/utils"
import { ReferenceDataType, withReferenceDataState } from "../../globalStates/ReferenceDataHOC"
import {
    fieldNameByLang,
    getElementFieldValueWithLanguage,
    makeSetHandlerWithLanguage,
    useLanguageSwitch
} from "../../utils/Language"
import SelectField from "../../components/Field/Select"
import TextField from "../../components/Field/Text"
import FilePicker from "../../components/FilePicker"
import CheckField from "../../components/Field/Check"
import Section from "../../components/Section"
import LanguageSwitch from "../languageSwitch"
import { isNullOrEmpty } from "../../utils/Arrays"
import branding from "../../branding/branding"
import { Person } from "../../globalStates/ExhibitorDataTypes"

export type PersonFunctionType = "team" | "eventdate" | "jobs"

function getWhitelist(type: PersonFunctionType): string[] {
    switch (type) {
        case "team":
            return branding.configuration.teamPage.sctPerson.personFunctionWhitelist
        case "eventdate":
            return branding.configuration.eventDatesPage.sctEventDates.personFunctionWhitelist
        case "jobs":
            return branding.configuration.jobsPage.sctJobs.personFunctionWhitelist
        default:
            return []
    }
}

function buildPersonFunctionsOptions(type: PersonFunctionType, refPersonsFunction: Immutable<Array<RefPersonFunctions>>) {
    const whitelist: string[] = getWhitelist(type)

    let personFunctions = refPersonsFunction.map((refPersonFunction) => ({
        id: refPersonFunction.value,
        label: refPersonFunction.name,
        value: refPersonFunction.value
    }))

    if (!isNullOrEmpty(whitelist)) {
        personFunctions = personFunctions.filter(
            (personFunction) => personFunction.id && whitelist.indexOf(personFunction.id) >= 0
        )
    }

    return personFunctions
}

interface PersonSectionProps {
    personState: State<Person, Validation>
    index: number
    onOrderChanged: (personId: string, index: number) => void
    onClickRemove: (personId: string) => void
    type: PersonFunctionType
}

const PersonSection: React.FC<PersonSectionProps & ReferenceDataType> = ({
    personState,
    refDataState,
    index,
    onOrderChanged,
    onClickRemove,
    type
}) => {
    const branding = useLanguageState().getStrings()
    const exiState = useExhibitorDataState()
    const person = useHookstate(personState)
    const personFunctions = useMemo(
        () => buildPersonFunctionsOptions(type, refDataState.getPersonFunctions()),
        [type, refDataState]
    )
    const isMonoLingual = branding.configuration.monoLingual ? "De" : "En"
    const { currentLanguage, onChangeLanguageHandler } = useLanguageSwitch(isMonoLingual)
    const sectionRef = useRef<HTMLDivElement>(null)

    const onChangeLogo = useCallback(
        (
            image: File,
            onUploadProgress: (progress: number) => void,
            onSuccessCallback: () => void,
            onErrorCallback: (reason: string) => void
        ) => {
            exiState
                .upload("LOGO", image, onUploadProgress)
                .then((response) => {
                    onSuccessCallback()
                    if (response) {
                        person.logoFileId.set(response.fileId)
                        person.logoUrl.set(response.fileUrl)
                    }
                })
                .catch((reason) => onErrorCallback(reason))
        },
        [exiState, person]
    )

    const onRemoveLogo = useCallback(() => {
        person.logoFileId.set("")
        person.logoUrl.set("")
    }, [person])

    const onClickRemoveHandler = useCallback(() => {
        onClickRemove(person.id.get())
    }, [onClickRemove, person])

    const [{ isDragging }, drag] = useDrag({
        type: "Person",
        item: { personId: person.id.get() },
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        }),
        end: (item, monitor) => {
            const didDrop = monitor.didDrop()
            if (!didDrop) {
            }
        }
    })
    drag(sectionRef)

    const [, drop] = useDrop({
        accept: "Person",
        hover({ personId: draggedPersonId }) {
            if (draggedPersonId !== person.id.get()) {
                onOrderChanged(draggedPersonId, index)
            }
        },
        drop: (droppedItem: any) => {
            // onOrderChanged(droppedItem.product, droppedItem.index, index)
        }
    })
    drop(sectionRef)

    const fullName =
        person.firstName.get() && person.lastName.get() ? `${person.firstName.get()} ${person.lastName.get()}` : undefined
    const readOnly = !getElementFieldValue(person, "isEditable") ?? false
    return (
        <Section
            ref={sectionRef}
            title={fullName || branding.configuration.teamPage.sctPerson.titleEmptyPerson}
            collapsible
            opacity={isDragging ? 0 : 1}
            onClickRemove={readOnly ? undefined : onClickRemoveHandler}
            entityState={person}
        >
            <Row>
                <Col xs={3}>
                    <FilePicker
                        name="logo"
                        rounded
                        readonly={readOnly}
                        label={branding.configuration.teamPage.sctPerson.lblProfileImage}
                        message={branding.configuration.teamPage.sctPerson.lblProfileImageMessage}
                        browseLabel={branding.configuration.teamPage.sctPerson.btnBrowseFiles}
                        deleteLabel={branding.configuration.teamPage.sctPerson.btnDeleteImg}
                        placeholderTitle={branding.configuration.teamPage.sctPerson.hntTitle}
                        placeholderText={branding.configuration.teamPage.sctPerson.hntProp}
                        value={person.logoUrl.get()}
                        onChangeImage={onChangeLogo}
                        onRemoveImage={onRemoveLogo}
                    />
                </Col>
                <Col xs={9}>
                    <Row>
                        <Col>
                            <LanguageSwitch
                                id={`person_${index}_language_switch`}
                                name={`person_${index}_languages`}
                                value={currentLanguage}
                                onChange={onChangeLanguageHandler}
                                entityState={personState}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextField
                                id="title"
                                name="title"
                                label={branding.configuration.teamPage.sctPerson.lblTitleTeam}
                                placeholder={branding.configuration.teamPage.sctPerson.hntTitleTeam}
                                readonly={readOnly}
                                value={getElementFieldValue(person, "title")}
                                onChange={makeSetHandler(person, "title")}
                            />
                        </Col>
                        <Col>
                            <TextField
                                id="firstName"
                                name="firstName"
                                label={branding.configuration.teamPage.sctPerson.lblFirstName}
                                placeholder={branding.configuration.teamPage.sctPerson.hntFirstName}
                                readonly={readOnly}
                                validate={makeValidationHandler(person, "firstName")}
                                value={getElementFieldValue(person, "firstName")}
                                onChange={makeSetHandler(person, "firstName")}
                                required
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextField
                                id="lastName"
                                name="lastName"
                                label={branding.configuration.teamPage.sctPerson.lblLastName}
                                placeholder={branding.configuration.teamPage.sctPerson.hntLastName}
                                readonly={readOnly}
                                validate={makeValidationHandler(person, "lastName")}
                                value={getElementFieldValue(person, "lastName")}
                                onChange={makeSetHandler(person, "lastName")}
                                required
                            />
                        </Col>
                        <Col>
                            <SelectField
                                id="function"
                                name="function"
                                label={branding.configuration.teamPage.sctPerson.lblRole}
                                placeholder={branding.configuration.teamPage.sctPerson.hntRole}
                                readonly={readOnly}
                                options={personFunctions}
                                validate={makeValidationHandler(person, "function")}
                                value={getElementFieldValue(person, "function")}
                                onChange={makeSetHandler(person, "function")}
                                required
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextField
                                id={fieldNameByLang("position", currentLanguage)}
                                name={fieldNameByLang("position", currentLanguage)}
                                label={branding.configuration.teamPage.sctPerson.lblPosition}
                                placeholder={branding.configuration.teamPage.sctPerson.hntPosition}
                                readonly={readOnly}
                                // validate={makeValidationHandlerWithLanguage(person, 'position', currentLanguage)}
                                value={getElementFieldValueWithLanguage(person, "position", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(person, "position", currentLanguage)}
                            />
                        </Col>
                        <Col>
                            <TextField
                                id="company"
                                name="company"
                                label={branding.configuration.teamPage.sctPerson.lblCompany}
                                placeholder={branding.configuration.teamPage.sctPerson.hntCompany}
                                readonly={readOnly}
                                value={getElementFieldValue(person, "company")}
                                onChange={makeSetHandler(person, "company")}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextField
                                id="phone"
                                name="phone"
                                label={branding.configuration.teamPage.sctPerson.lblPhone}
                                placeholder={branding.configuration.teamPage.sctPerson.hntPhone}
                                readonly={readOnly}
                                //validate={makeValidationHandler(person, 'phone')}
                                value={getElementFieldValue(person, "phone")}
                                onChange={makeSetHandler(person, "phone")}
                            />
                        </Col>
                        <Col>
                            <TextField
                                id="email"
                                name="email"
                                label={branding.configuration.teamPage.sctPerson.lblEmail}
                                placeholder={branding.configuration.teamPage.sctPerson.hntEmail}
                                readonly={readOnly}
                                validate={makeValidationHandler(person, "email")}
                                value={getElementFieldValue(person, "email")}
                                onChange={makeSetHandler(person, "email")}
                                required
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <CheckField
                                id={`phoneVisible${index}`}
                                name="phoneVisible"
                                label={branding.configuration.teamPage.sctPerson.lblPhoneVisible}
                                value={getElementFieldValue(person, "phoneVisible")}
                                onChange={makeCheckHandler(person, "phoneVisible")}
                            />
                        </Col>
                        <Col>
                            <CheckField
                                id={`emailVisible${index}`}
                                name="emailVisible"
                                label={branding.configuration.teamPage.sctPerson.lblEmailVisible}
                                value={getElementFieldValue(person, "emailVisible")}
                                onChange={makeCheckHandler(person, "emailVisible")}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
            {/* <br />
            <br />
            <Row>
                <Col>
                    <TextAreaField
                        name={fieldNameByLang("description", currentLanguage)}
                        label={branding.configuration.teamPage.sctPerson.lblDescriptionDetail}
                        readonly={readOnly}
                        placeholder={branding.configuration.teamPage.sctPerson.hntDescriptionDetail}
                        value={getElementFieldValueWithLanguage(person, "description", currentLanguage)}
                        onChange={makeSetHandlerWithLanguage(person, 'description', currentLanguage)}
                        limit={4000}
                        style={{ height: '100px' }}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <TextAreaField
                        name={fieldNameByLang("teaser", currentLanguage)}
                        label={branding.configuration.teamPage.sctPerson.lblDescriptionShort}
                        readonly={readOnly}
                        placeholder={branding.configuration.teamPage.sctPerson.hntDescriptionShort}
                        value={getElementFieldValueWithLanguage(person, "teaser", currentLanguage)}
                        onChange={makeSetHandlerWithLanguage(person, 'teaser', currentLanguage)}
                        limit={4000}
                        style={{ height: '100px' }}
                    />
                </Col>
            </Row> */}
        </Section>
    )
}
export default React.memo(withReferenceDataState(PersonSection))
